/**
 * 错误码总表 —— 契约 §0.2（冻结）。
 *
 * 设计取舍：用「常量对象 + 值联合类型」而不是 TS `enum`。
 * enum 会生成运行时反向映射对象，小程序端包体与 tree-shaking 都不友好；
 * const 对象在保持 `ErrorCode.INVALID_PARAM` 书写体验的同时可被安全摇树。
 */

export const ErrorCode = {
  OK: 0,
  INVALID_PARAM: 40001,
  UNAUTHORIZED: 40100,
  TOKEN_EXPIRED: 40101,
  REFRESH_INVALID: 40102,
  FORBIDDEN: 40300,
  RISK_BLOCKED: 40301,
  NOT_FOUND: 40400,
  CONFLICT: 40900,
  QUOTA_EXCEEDED: 42900,
  RATE_LIMITED: 42901,
  INTERNAL: 50000,
  UPSTREAM_ERROR: 50200,
  UPSTREAM_TIMEOUT: 50201,
  MAINTENANCE: 50300,
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** 业务码 → HTTP 状态码（契约 §0.2 "HTTP" 列，逐字一致） */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = Object.freeze({
  [ErrorCode.OK]: 200,
  [ErrorCode.INVALID_PARAM]: 400,
  [ErrorCode.UNAUTHORIZED]: 401,
  [ErrorCode.TOKEN_EXPIRED]: 401,
  [ErrorCode.REFRESH_INVALID]: 401,
  [ErrorCode.FORBIDDEN]: 403,
  [ErrorCode.RISK_BLOCKED]: 403,
  [ErrorCode.NOT_FOUND]: 404,
  [ErrorCode.CONFLICT]: 409,
  [ErrorCode.QUOTA_EXCEEDED]: 429,
  [ErrorCode.RATE_LIMITED]: 429,
  [ErrorCode.INTERNAL]: 500,
  [ErrorCode.UPSTREAM_ERROR]: 502,
  // 契约表格里 UPSTREAM_TIMEOUT 的 HTTP 列是 504（网关超时语义），业务码仍是 50201
  [ErrorCode.UPSTREAM_TIMEOUT]: 504,
  [ErrorCode.MAINTENANCE]: 503,
});

/** 业务码 → 默认中文文案；调用方可覆盖为更精确的提示 */
export const ERROR_MESSAGE: Readonly<Record<ErrorCode, string>> = Object.freeze({
  [ErrorCode.OK]: 'ok',
  [ErrorCode.INVALID_PARAM]: '参数校验失败',
  [ErrorCode.UNAUTHORIZED]: '未登录或令牌无效',
  [ErrorCode.TOKEN_EXPIRED]: 'accessToken 已过期',
  [ErrorCode.REFRESH_INVALID]: 'refreshToken 已失效，请重新登录',
  [ErrorCode.FORBIDDEN]: '无权访问该资源',
  [ErrorCode.RISK_BLOCKED]: '该问题超出服务范围',
  [ErrorCode.NOT_FOUND]: '资源不存在',
  [ErrorCode.CONFLICT]: '并发写冲突或重复幂等键',
  [ErrorCode.QUOTA_EXCEEDED]: '额度不足',
  [ErrorCode.RATE_LIMITED]: '请求过于频繁，请稍后重试',
  [ErrorCode.INTERNAL]: '服务端内部错误',
  [ErrorCode.UPSTREAM_ERROR]: 'AI 服务暂时不可用，本次未消耗额度',
  [ErrorCode.UPSTREAM_TIMEOUT]: 'AI 服务响应超时，本次未消耗额度',
  [ErrorCode.MAINTENANCE]: '系统维护中',
});

export function httpStatusOf(code: ErrorCode): number {
  return ERROR_HTTP_STATUS[code];
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'number' && value in ERROR_HTTP_STATUS;
}

/** 统一响应信封（契约 §0.1） */
export interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
  details?: Record<string, unknown>;
}

/** 失败信封：data 恒为 null */
export interface ApiError {
  code: number;
  message: string;
  data: null;
  requestId: string;
  details?: Record<string, unknown>;
}

export interface AppExceptionOptions {
  /** 中断业务流程的底层原因，仅用于日志，绝不透传给客户端 */
  cause?: unknown;
  /** 覆盖默认 HTTP 状态码的少数场景（如维护窗口强制 503） */
  httpStatus?: number;
}

/**
 * 业务异常基类。
 *
 * 刻意不在此处 import NestJS 的 HttpException：
 * contracts 必须零运行时依赖，且要被小程序端引用，不能拖入后端框架。
 * apps/server 只需 `class DomainException extends AppException implements HttpException`
 * 或在 AllExceptionsFilter 里读取 code/httpStatus 完成转换即可（见 docs/01-architecture.md §ADR-1）。
 */
export class AppException extends Error {
  readonly code: ErrorCode;
  readonly details: Record<string, unknown> | undefined;
  readonly httpStatus: number;
  override readonly cause: unknown;

  constructor(
    code: ErrorCode,
    message?: string,
    details?: Record<string, unknown>,
    options?: AppExceptionOptions,
  ) {
    const resolvedMessage = message ?? ERROR_MESSAGE[code];
    super(resolvedMessage);
    this.name = 'AppException';
    this.code = code;
    this.details = details;
    this.httpStatus = options?.httpStatus ?? ERROR_HTTP_STATUS[code];
    this.cause = options?.cause;
    // 让 instanceof 在 ES2022 目标下对 extends Error 的子类继续成立
    Object.setPrototypeOf(this, new.target.prototype);
  }

  toEnvelope(requestId: string): ApiError {
    return {
      code: this.code,
      message: this.message,
      data: null,
      requestId,
      ...(this.details === undefined ? {} : { details: this.details }),
    };
  }
}

/** 判断任意抛出物是否为可安全按契约信封返回的业务异常 */
export function isAppException(error: unknown): error is AppException {
  return error instanceof AppException;
}

/** zod 校验失败 → details.field 用的字段路径格式化（避免前端自己拼 `.issues[0].path`） */
export function formatIssuePath(path: readonly (string | number)[]): string {
  return path.map((segment) => String(segment)).join('.');
}
